import React from "react";
import { clsx } from "clsx";

export const WarehouseButtonsFooter = ({
  children,
  error,
  className = "",
  title,
  text,
  extraContent,
  isCameraActive = false,
}) => {
  return (
    <div
      className={clsx(
        isCameraActive
          ? "bg-transparent relative z-50"
          : "border-t border-[#282F44] backdrop-blur-[8px] bg-[#030711]",
        className
      )}
    >
      <div className="p-4 sm:p-6">
        {(title || text) && (
          <div className="mb-4 sm:mb-6">
            {title && (
              <h1 className="text-[22px] sm:text-[29px] font-bold text-white leading-[28px] sm:leading-[37px] tracking-[-0.01em] text-center mb-2">
                {title}
              </h1>
            )}
            {text && (
              <p className="text-[14px] sm:text-[18px] font-normal text-[#C6CCE8] leading-[18px] sm:leading-[23px] text-center">
                {text}
              </p>
            )}
          </div>
        )}
        {extraContent && <div className="mb-4 sm:mb-6">{extraContent}</div>}
        {children}
        {error && (
          <p className="text-sm sm:text-base text-[#C12960] mt-3 sm:mt-4 text-center">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export const WarehouseButton = React.forwardRef(
  (
    {
      children,
      onClick,
      variant = "default",
      disabled = false,
      className = "",
      ...props
    },
    ref
  ) => {
    const baseClasses =
      "w-full rounded-lg font-normal py-2.5 sm:py-3.5 m-0 h-14 sm:h-20 text-lg sm:text-2xl [&_*]:!text-lg sm:[&_*]:!text-2xl";

    const variants = {
      default:
        "border border-[#282F44] bg-transparent text-white hover:bg-[#282F44]/20",
      reject:
        "border border-[#C12960] bg-transparent text-[#C12960] hover:bg-[#282F44]/20",
      primary: clsx(
        "border border-transparent",
        disabled
          ? "bg-[#173991]/50 text-white/50 cursor-not-allowed"
          : "bg-[#173991] text-white hover:bg-[#1e4bb8]"
      ),
    };

    return (
      <button
        ref={ref}
        className={clsx(baseClasses, variants[variant], className)}
        onClick={onClick}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }
);

export const WarehouseButtonGroup = ({ children, className = "", columns }) => {
  const childCount = React.Children.count(children);
  const actualColumns = columns || childCount;

  let gridClass = "";

  if (columns) {
    // If columns prop is provided, use inline styles
    gridClass = "";
  } else {
    // Handle specific child counts with explicit classes
    switch (childCount) {
      case 2:
        gridClass = "grid-cols-1 sm:grid-cols-2";
        break;
      case 3:
        gridClass = "grid-cols-1 sm:grid-cols-3";
        break;
      case 4:
        gridClass = "grid-cols-2 sm:grid-cols-4";
        break;
      default:
        gridClass =
          childCount === 1
            ? "grid-cols-1"
            : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
    }
  }

  return (
    <div
      className={clsx("grid gap-4 sm:gap-8", gridClass, className)}
      style={
        columns
          ? {
              gridTemplateColumns: `repeat(${actualColumns}, minmax(0, 1fr))`,
            }
          : undefined
      }
    >
      {children}
    </div>
  );
};
